/**
 * Tracking foundation (T1) — single source of truth for analytics.
 *
 * - GA4 loads on EVERY host with automatic page views disabled; the router
 *   (App.js <AnalyticsTracker/>) sends exactly one page_view per route change.
 * - Production is ONLY artistscanner.ai / www.artistscanner.ai. Every other host
 *   (preview, emergent, localhost) and every logged-in admin/team session is
 *   marked traffic_type = 'internal'. The Meta Pixel never loads off production
 *   and never for an internal (admin/team) session.
 * - First/last touch attribution lives in localStorage (as_first_touch /
 *   as_last_touch) and is exposed via getAttribution().
 * - Every event goes through track(eventName, params): it sanitises params
 *   (drops anything that looks like a phone/email/name/OTP/token) and, when a
 *   Meta mapping exists, mirrors the event to the pixel.
 */

import { afterLoad } from '../lib/afterLoad';

const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const CLARITY_ID = process.env.REACT_APP_CLARITY_PROJECT_ID;

// Production is the live domain only. Everything else is internal traffic.
const PRODUCTION_HOSTS = ['artistscanner.ai', 'www.artistscanner.ai'];
// Staff/team roles — a logged-in session with any of these is internal too.
const INTERNAL_ROLES = ['admin', 'management', 'designer', 'artist_manager'];

// Events mirrored to the Meta Pixel (standard events). Everything else is GA4 only.
const META_MAP = {
  contact_reveal: 'Contact',
  post_requirement_submitted: 'Lead',
  artist_signup_completed: 'CompleteRegistration',
  search: 'Search',
};

// Attribution fields captured from the URL on landing.
const ATTR_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid'];
const FT_KEY = 'as_first_touch';
const LT_KEY = 'as_last_touch';

let internalUser = false; // flipped true once a logged-in admin/team member is seen

const isProductionHost = () => {
  try { return PRODUCTION_HOSTS.includes(window.location.hostname); } catch (e) { return false; }
};
// Internal when off production OR an admin/team session is active.
const isInternal = () => !isProductionHost() || internalUser;
const metaReady = () => typeof window !== 'undefined' && !!window.fbq;

// ----------------------------------------------------------------- sanitiser
// A param is dropped when its KEY or its VALUE looks like PII (phone, email,
// name, OTP, token). Only IDs, categories, cities, bands and counts survive.
const SENSITIVE_KEY = /phone|mobile|tel|otp|token|e-?mail|passw|pwd|secret|whatsapp|aadhaar|name|fname|lname|auth|session|jwt|otp_code|verification/i;

// Keys that are always allowed to pass (IDs, categories, cities, bands, counts).
const ALLOWED_KEYS = new Set([
  'artist_id', 'category', 'categories', 'city', 'area', 'budget_band', 'band',
  'result_count', 'matched_count', 'count', 'step', 'source_page', 'occasion',
  'query_type', 'device', 'page', 'user_role', 'collection', 'n', 'event_type',
  'booking_id', 'profile_status', 'traffic_type', 'answer', 'rating',
  'reveal_id', 'rank_score', 'action', 'slug',
]);

function looksSensitiveValue(v) {
  if (v === null || v === undefined || typeof v === 'boolean') return false;
  if (typeof v === 'number') return String(Math.abs(v)).length >= 7; // long numbers ~ phone
  const s = String(v);
  if (/\S+@\S+\.\S+/.test(s)) return true;          // email
  if (/\d{6,}/.test(s)) return true;                // any 6+ digit run (phone/otp/free-text)
  if ((s.match(/\d/g) || []).length >= 7) return true; // spaced phone e.g. 98765 43210
  if (s.length >= 24 && /[A-Za-z]/.test(s) && /\d/.test(s)) return true; // token-ish
  return false;
}

// Strip phone/otp/token query params from a path+query string.
function sanitizePath(pathWithQuery) {
  try {
    const raw = String(pathWithQuery || '');
    const qIdx = raw.indexOf('?');
    if (qIdx === -1) return raw;
    const path = raw.slice(0, qIdx);
    const qs = new URLSearchParams(raw.slice(qIdx + 1));
    const keep = new URLSearchParams();
    for (const [k, val] of qs.entries()) {
      if (SENSITIVE_KEY.test(k) || looksSensitiveValue(val)) continue;
      keep.set(k, val);
    }
    const kept = keep.toString();
    return kept ? `${path}?${kept}` : path;
  } catch (e) {
    return String(pathWithQuery || '').split('?')[0];
  }
}

function sanitizeParams(params) {
  const out = {};
  Object.keys(params || {}).forEach((k) => {
    let v = params[k];
    if (v === null || v === undefined || v === '') return;
    if (SENSITIVE_KEY.test(k)) return; // drop by key
    if (k === 'source_page') { out[k] = sanitizePath(v); return; }
    if (ALLOWED_KEYS.has(k)) { out[k] = v; return; } // IDs/categories/cities/bands/counts
    if (looksSensitiveValue(v)) return; // drop by value
    out[k] = v;
  });
  return out;
}

// ----------------------------------------------------------------- init
export const initializeAnalytics = () => {
  try {
    if (GA4_ID) initGA4();
    if (isProductionHost() && CLARITY_ID) initClarity();
  } catch (e) {
    /* never let analytics break the app */
  }
};

function initGA4() {
  if (window.__ga4Init) return;
  window.__ga4Init = true;

  // Stub is set up IMMEDIATELY so page_view / events queue into dataLayer and are
  // never dropped, even before the (deferred) gtag.js script has downloaded.
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  const cfg = { send_page_view: false, cookie_flags: 'SameSite=None;Secure' };
  if (isInternal()) cfg.traffic_type = 'internal';
  window.gtag('config', GA4_ID, cfg);

  // R6 — gtag.js downloads only after the page has loaded, so it never competes with the
  // first paint or the API calls. Queued hits are flushed once it loads (page_view kept).
  const loadScript = () => {
    if (document.getElementById('ga4-src')) return;
    const script = document.createElement('script');
    script.id = 'ga4-src';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(script);
  };
  afterLoad(loadScript, 0);
}

// Meta Pixel — deferred and gated: production host only, never for an internal
// (admin/team) session. Called from the router once auth state is known.
export const initMetaPixel = (user) => {
  try {
    if (!META_PIXEL_ID || !isProductionHost()) return;
    if (user && INTERNAL_ROLES.includes(user.role)) return;
    if (window.fbq) return;
    // Standard pixel stub (events queue); R6 — fbevents.js itself loads after the page.
    const n = function () { if (n.callMethod) n.callMethod.apply(n, arguments); else n.queue.push(arguments); };
    window.fbq = n;
    if (!window._fbq) window._fbq = n;
    n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
    afterLoad(() => addScript('https://connect.facebook.net/en_US/fbevents.js'), 0);
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
  } catch (e) { /* ignore */ }
};

const addScript = (src) => {
  const t = document.createElement('script');
  t.async = true;
  t.src = src;
  const s = document.getElementsByTagName('script')[0];
  if (s && s.parentNode) s.parentNode.insertBefore(t, s); else document.head.appendChild(t);
};

// Clarity stub (calls queue); R6 — the tag loads after the page.
function initClarity() {
  if (window.clarity) return;
  window.clarity = function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
  afterLoad(() => addScript(`https://www.clarity.ms/tag/${CLARITY_ID}`), 0);
}

// ----------------------------------------------------------------- attribution
function readUrlAttribution() {
  const out = {};
  try {
    const p = new URLSearchParams(window.location.search);
    ATTR_FIELDS.forEach((k) => { const v = p.get(k); if (v) out[k] = v; });
  } catch (e) { /* ignore */ }
  return out;
}

function buildTouch(params) {
  return {
    ...params,
    landing_path: (() => { try { return window.location.pathname; } catch (e) { return ''; } })(),
    referrer: (() => { try { return document.referrer || ''; } catch (e) { return ''; } })(),
    ts: new Date().toISOString(),
  };
}

// Writes first-touch once (never overwritten) and refreshes last-touch whenever a
// new set of attribution params arrives. Safe to call on every route change.
export const captureAttribution = () => {
  try {
    const params = readUrlAttribution();

    if (!localStorage.getItem(FT_KEY)) {
      localStorage.setItem(FT_KEY, JSON.stringify(buildTouch(params)));
    }

    const hasAny = Object.keys(params).length > 0;
    if (hasAny) {
      let prev = null;
      try { prev = JSON.parse(localStorage.getItem(LT_KEY) || 'null'); } catch (e) { prev = null; }
      const sig = (o) => ATTR_FIELDS.map((k) => (o && o[k]) || '').join('|');
      if (sig(prev) !== sig(params)) {
        localStorage.setItem(LT_KEY, JSON.stringify(buildTouch(params)));
      }
    } else if (!localStorage.getItem(LT_KEY)) {
      localStorage.setItem(LT_KEY, JSON.stringify(buildTouch(params)));
    }
  } catch (e) { /* storage disabled — ignore */ }
};

// P22 — a visit to an artist's share link (/a/{slug}) records artist_share as
// the source. First-touch is written once; last-touch is always refreshed so the
// most recent artist gets credit for the conversion.
export const recordArtistShareTouch = (slug) => {
  try {
    const touch = buildTouch({ utm_source: 'artist_share', utm_content: slug || '' });
    if (!localStorage.getItem(FT_KEY)) localStorage.setItem(FT_KEY, JSON.stringify(touch));
    localStorage.setItem(LT_KEY, JSON.stringify(touch));
  } catch (e) { /* storage disabled — ignore */ }
};

export const getAttribution = () => {  try {
    return {
      first_touch: JSON.parse(localStorage.getItem(FT_KEY) || 'null'),
      last_touch: JSON.parse(localStorage.getItem(LT_KEY) || 'null'),
    };
  } catch (e) {
    return { first_touch: null, last_touch: null };
  }
};

// ----------------------------------------------------------------- events
export const track = (eventName, params = {}) => {
  const clean = sanitizeParams(params);
  const ga = { ...clean };
  if (isInternal()) ga.traffic_type = 'internal';
  try { if (window.gtag && GA4_ID) window.gtag('event', eventName, ga); } catch (e) { /* ignore */ }
  try { if (metaReady() && META_MAP[eventName]) window.fbq('track', META_MAP[eventName], clean); } catch (e) { /* ignore */ }
};

// Back-compat alias — existing callers used trackEvent(name, params).
export const trackEvent = (eventName, params = {}) => track(eventName, params);

export const trackPageView = (pagePath, pageTitle) => {
  const page_path = sanitizePath(pagePath);
  const p = { page_path, page_title: pageTitle };
  try { p.page_location = window.location.origin + page_path; } catch (e) { /* ignore */ }
  if (isInternal()) p.traffic_type = 'internal';
  try { if (window.gtag && GA4_ID) window.gtag('event', 'page_view', p); } catch (e) { /* ignore */ }
  try { if (metaReady()) window.fbq('track', 'PageView'); } catch (e) { /* ignore */ }
};

// Marks the session internal when an admin/team member is signed in, and records
// a coarse (non-PII) user_role property.
export const setUserProperties = (user) => {
  if (!user) return;
  try {
    if (INTERNAL_ROLES.includes(user.role)) {
      internalUser = true;
      if (window.gtag && GA4_ID) window.gtag('set', { traffic_type: 'internal' });
    }
    if (window.gtag && GA4_ID) window.gtag('set', 'user_properties', { user_role: user.role || 'guest' });
    const uid = user.user_id || user.id;
    if (window.clarity && CLARITY_ID && uid) window.clarity('identify', uid);
  } catch (e) { /* ignore */ }
};

// ---------------------------------------------------------------- back-compat
// Thin wrappers so pre-T1 callers keep working; all route through track().
export const trackArtistSignupStarted = () => track('artist_signup_started', {});
export const trackArtistSignupCompleted = () => track('artist_signup_completed', {});
export const trackProfileSubmitted = (artistId) => track('profile_submitted', { artist_id: artistId });
export const trackProfileApproved = (artistId) => track('profile_approved', { artist_id: artistId });
export const trackBookingRequestSent = (data = {}) => track('booking_request_sent', { artist_id: data.artistId });
export const trackBookingAccepted = (bookingId) => track('booking_accepted', { booking_id: bookingId });
export const trackPaymentCompleted = (data = {}) => track('payment_completed', { booking_id: data.bookingId });

export default {
  initializeAnalytics,
  initMetaPixel,
  captureAttribution,
  getAttribution,
  recordArtistShareTouch,
  track,
  trackEvent,
  trackPageView,
  setUserProperties,
  trackArtistSignupStarted,
  trackArtistSignupCompleted,
  trackProfileSubmitted,
  trackProfileApproved,
  trackBookingRequestSent,
  trackBookingAccepted,
  trackPaymentCompleted,
};
